import { useEffect, useRef } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { trackEventViewed } from '@/lib/analytics/events';
import { useMember, useAuthedSession } from '@/lib/use-session';
import { eventsKeys } from '@/lib/queries/events';
import { groupKeys } from '@/lib/queries/groups';
import { adaptEventDetail } from '@/components/events/event-adapters';
import { useEventAdapterContext } from '@/components/events/event-adapters.client';
import type { EventDetail as EventDetailDomain } from '@leanin/shared/views/event';
import { EventDetailView } from './-components/EventDetailView';
import { REGISTER_INTENT_PARAM } from '@/components/events/RegisterNeedsAccountDialog';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';

type EventDetailSearch = { readonly [REGISTER_INTENT_PARAM]?: true };

// Public: a signed-out visitor opens the page of any event on the public
// Events directory (the server enforces which); signed-in members get the
// full page, exactly as before.
export const Route = createFileRoute('/_public/events/$id/')({
  validateSearch: (raw: Record<string, unknown>): EventDetailSearch =>
    raw[REGISTER_INTENT_PARAM] === 1 || raw[REGISTER_INTENT_PARAM] === '1'
      ? { [REGISTER_INTENT_PARAM]: true }
      : {},
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: EventDetailPage,
});

function EventDetailPage() {
  const { id } = Route.useParams();
  const registerIntent = Route.useSearch({ select: (s) => s[REGISTER_INTENT_PARAM] === true });
  const member = useMember();
  const signedIn = member !== null;
  const session = useAuthedSession();
  const eventCtx = useEventAdapterContext();

  const detailQuery = useQuery(eventsKeys.detail(id));
  // Locked viewers (members-only event, viewer outside the group) never
  // see the attendees section, so the query stays disabled for them.
  const attendeesQuery = useQuery({
    ...eventsKeys.attendees(id, 6),
    enabled: signedIn && detailQuery.data != null && detailQuery.data.viewerCanRsvp,
  });

  const detail = detailQuery.data;
  const isOrganizer =
    detail != null &&
    session != null &&
    detail.organizerUserId !== null &&
    detail.organizerUserId === session.userId;
  const isPlatformAdmin = session?.platformRole === 'admin';
  const needsGroupRole = signedIn && detail != null && !isOrganizer && !isPlatformAdmin;

  const groupRoleQuery = useQuery({
    ...groupKeys.viewerAccess(detail?.groupId ?? ''),
    select: (access) => access.role,
    enabled: needsGroupRole,
  });

  const viewedEventIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (detail == null || viewedEventIdRef.current === detail.id) return;
    viewedEventIdRef.current = detail.id;
    trackEventViewed({ is_rsvped: detail.viewerRsvpStatus !== null });
  }, [detail]);

  // 404 → branded NotFoundPage directly. (The previous duck-type check
  // for `error.status === 404` never fired because the openapi-fetch
  // error path threw a generic DomainError without a `status` field.)
  if (isNotFoundApiError(detailQuery.error)) return <NotFoundPage />;

  const event =
    detail != null
      ? adaptEventDetail(detail as unknown as EventDetailDomain, eventCtx)
      : null;

  const isLeader = groupRoleQuery.data === 'leader';
  const canManage = isOrganizer || isPlatformAdmin || isLeader;

  return (
    <EventDetailView
      eventId={id}
      event={event}
      attendees={attendeesQuery.data ?? null}
      canManage={canManage}
      isOrganizer={isOrganizer}
      signedIn={signedIn}
      registerIntent={registerIntent}
    />
  );
}
